/* eslint-disable @typescript-eslint/no-explicit-any */
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { fetchSearchData } from './useSearchData';
import { useRecentSearches } from './useRecentSearches';
import {
  getProwlCategoryCounts,
  getProwlFacetCount,
  type ProwlFacetCountParams,
} from '../lib/prowl/heroCounts';
import {
  CategoriesAndSubResponse,
  CategoriesResponse,
  FetchSearchDataParams,
  FetchSearchDataResponse,
  IBookingQuery,
  IServiceQuery,
  ReviewsResponse,
  ReviewsResponseById,
  SubCategoriesResponse,
} from '../utils/type';
import {
  getAllCategory,
  getAllCategoryAndSub,
  getAllSubCategory,
  getCategoryById,
} from '../service/categoryService';
import {
  getAllPublicServiceByProvider,
  getAllServiceByQuery,
  getServiceByQuery,
  getServiceDataById,
  getServiceDataByProvider,
  getServiceSuggestions,
  getSearchSuggestions,
} from '../service/serviceService';
import {
  getAllBookingsByQuery,
  getStaffById,
  getUserBookings,
  type BookingsResponse,
  type PaginationParams,
} from '../service/bookingService';
import {
  getBusinessDetailsBySlug,
  getBusinessSettings,
  getProviderById,
  getProviderByIds,
  getProviderDetailsByProvider,
  getProviderPortfolioCardDetails,
} from '../service/ProviderService';
import { getUserById } from '../service/userService';
import {
  getAllReviewsForProvider,
  getFilteredReviews,
  getOverallRating,
  getReviewByBookingId,
  getReviewOverall,
  getReviewsById,
  getReviewsByServiceId,
  getReviewsByUserId,
} from '../service/reviewService';
import {
  getBusinessBySlug,
  getBusinessPublicStats,
  getPublicServicesByBusiness,
} from '../service/businessService';
import { getAllProviderCertificate } from '../service/providerCertificate';
import { autoComplete } from '../service/ReverseGeocode';
import {
  createSubscription,
  getAllMembershipPlans,
  getLandingPage,
} from '../service/landingPageService';
import { addToast } from '@heroui/react';
import { useAuth } from '../context/AuthContext';
import {
  getNotifications,
  getUnreadNotifications,
} from '../service/notificationService';
import {
  getUserBySessionId,
  checkSessionStatus,
  handleLogin,
} from '../feature-module/components/auth/apiAuth';
import { QueryKey } from './queryKey';
import {
  setPersistedActiveBusinessId,
  clearPersistedActiveBusinessId,
} from '../utils/sessionHeaderStorage';
import {
  getMyJobs,
  getJobById,
  getJobThread,
  getJobRevisions,
  type JobsResponse,
} from '../service/jobService';
import { getMyPackages } from '../service/packageService';
import {
  CustomerPackage,
  Job,
  JobStatus,
  JobThread,
  QuoteRevision,
} from '../utils/type';

interface UseNotificationsOptions {
  enabled?: boolean;
  refetchInterval?: number;
}

const NOTIFICATION_ROLE = 'customer';

export const useSearchData = (filter: Record<string, any>) => {
  return useQuery({
    queryKey: ['searchData', filter],
    queryFn: () => fetchSearchData({ filter }), // Wrap the function call properly
    enabled: false, // Prevent automatic fetching
  });
};

export const useSearchDataMutation = () => {
  return useMutation<FetchSearchDataResponse, Error, FetchSearchDataParams>(
    { mutationFn: fetchSearchData } // Pass the `fetchSearchData` function as a property of an object
  );
};

export const useCategoryQuery = () => {
  return useQuery<CategoriesResponse>({
    queryKey: ['categories'],
    queryFn: getAllCategory,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });
};

export const useCategoryAndSubQuery = (limit: number) => {
  return useQuery<CategoriesAndSubResponse>({
    queryKey: ['categoriesAndSub', limit],
    queryFn: () => getAllCategoryAndSub(limit),
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });
};

export const useSubCategoryQuery = () => {
  return useQuery<SubCategoriesResponse>({
    queryKey: ['subcategories'],
    queryFn: getAllSubCategory,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });
};

export const useFetchCategoryById = (id?: string) => {
  return useQuery({
    queryKey: ['GET_SERVICE_BY_ID', id],
    queryFn: () =>
      id ? getCategoryById(id) : Promise.reject('No ID provided'),
    staleTime: 1 * 60 * 1000,
    enabled: !!id,
    refetchOnWindowFocus: false,
  });
};

export const useFetchServiceDataById = (id?: string) => {
  return useQuery({
    queryKey: ['fetchServiceDataById', id],
    queryFn: () =>
      id ? getServiceDataById(id) : Promise.reject('No ID provided'),
    staleTime: 5000,
    enabled: !!id,
    retry: 1,
    refetchOnWindowFocus: false,
  });
};

export const useFetchServiceDataByProvider = (
  page?: number,
  limit?: number,
  id?: string
) => {
  return useQuery({
    queryKey: ['fetchServiceDataById', page, limit, id],
    queryFn: ({ queryKey }) => {
      const [, pageArg = 1, limitArg = 2] = queryKey as [
        string,
        number?,
        number?,
      ];
      return getServiceDataByProvider({
        page: pageArg,
        limit: limitArg,
        id: id,
      });
    },
    retry: 1,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });
};

// Bookings (React Query)
export const useFetchUserBookings = (
  params: PaginationParams = {},
  options?: { refetchInterval?: number; enabled?: boolean }
) => {
  const { userId, page = 1, limit = 10, status } = params || {};
  return useQuery<BookingsResponse>({
    queryKey: ['USER_BOOKINGS', userId, page, limit, status],
    queryFn: async () => {
      if (!userId) {
        console.warn('useFetchUserBookings: userId is required');
        return {
          bookings: [],
          total: 0,
          page: 1,
          pages: 0,
          limit: 10,
          totalPages: 0,
        };
      }
      try {
        return await getUserBookings({ userId, page, limit, status });
      } catch (error: any) {
        console.error('useFetchUserBookings error:', error);
        // Return empty result on error instead of throwing
        if (error.response?.status === 403 || error.response?.status === 401) {
          throw error; // Re-throw auth errors to trigger error state
        }
        return {
          bookings: [],
          total: 0,
          page: 1,
          pages: 0,
          limit: 10,
          totalPages: 0,
        };
      }
    },
    enabled: !!userId && (options?.enabled ?? true),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
    placeholderData: keepPreviousData,
    retry: (failureCount, error: any) => {
      // Don't retry on authentication errors
      if (error?.response?.status === 403 || error?.response?.status === 401) {
        return false;
      }
      // Retry other errors up to 2 times
      return failureCount < 2;
    },
    ...(options?.refetchInterval
      ? {
          refetchInterval: options.refetchInterval,
          refetchIntervalInBackground: true,
        }
      : {}),
  });
};

export const useFetchStaffById = (id?: string) => {
  return useQuery({
    queryKey: ['fetchStaffById', id],
    queryFn: () => (id ? getStaffById(id) : Promise.reject('No ID provided')),
    // staleTime: 5000,
    enabled: !!id,
    refetchOnWindowFocus: false,
  });
};

///PROVIDER
export const useFetchProviderById = (id?: string) => {
  return useQuery({
    queryKey: ['GET_PROVIDER_BY_ID', id],
    queryFn: () =>
      id ? getProviderById(id) : Promise.reject('No ID provided'),
    staleTime: 30 * 60 * 1000,
    enabled: !!id,
    refetchOnWindowFocus: false,
  });
};

export const useFetchProviderByIds = (ids?: string[]) => {
  return useQuery({
    queryKey: ['GET_PROVIDER_BY_ID', ids],
    queryFn: () =>
      ids ? getProviderByIds(ids) : Promise.reject('No IDs provided'),
    staleTime: 30 * 60 * 1000,
    enabled: !!ids,
    refetchOnWindowFocus: false,
  });
};

// GET USER BY ID
export const useFetchUserById = (id: string) => {
  return useQuery({
    queryKey: ['FETCH_USER_BY_ID', id],
    queryFn: () => (id ? getUserById(id) : Promise.reject('No ID provided')),
    staleTime: 5 * 60 * 1000,
    enabled: !!id,
    refetchOnWindowFocus: false,
  });
};

// GET USER BY ID
export const useFetchReviewByUserId = (
  id?: string,
  params: PaginationParams = {}
) => {
  return useQuery({
    queryKey: [
      'FETCH_REVIEW_BY_USER_ID',
      id,
      params.page,
      params.limit,
      params.rating,
    ],
    queryFn: () =>
      id ? getReviewsByUserId(id, params) : Promise.reject('No ID provided'),
    staleTime: 5 * 60 * 1000,
    enabled: !!id,
    refetchOnWindowFocus: false,
  });
};

export const useFetchReviewByBookingId = (id?: string) => {
  return useQuery({
    queryKey: ['FETCH_REVIEW_BY_BOOKING_ID', id],
    queryFn: () =>
      id ? getReviewByBookingId(id) : Promise.reject('No ID provided'),
    staleTime: 5 * 60 * 1000,
    enabled: !!id,
    refetchOnWindowFocus: false,
  });
};

export const useFetchReviewsByServiceId = (
  id?: string,
  params: PaginationParams = {}
) => {
  return useQuery<ReviewsResponse>({
    queryKey: ['FETCH_REVIEW_BY_SERVICE_ID', id, params],
    queryFn: () =>
      id ? getReviewsByServiceId(id, params) : Promise.reject('No ID provided'),
    staleTime: 5 * 60 * 1000,
    enabled: !!id,
    refetchOnWindowFocus: false,
  });
};

export const useFetchReviewsById = (id?: string) => {
  return useQuery<ReviewsResponseById>({
    queryKey: ['FETCH_REVIEW_BY_ID', id],
    queryFn: () => (id ? getReviewsById(id) : Promise.reject('No ID provided')),
    staleTime: 5 * 60 * 1000,
    enabled: !!id,
    refetchOnWindowFocus: false,
  });
};

//GET ALL REVIEWS FOR PROVIDER
export const useFetchAllReviews = (
  page?: number,
  limit?: number,
  id?: string
) => {
  return useQuery({
    queryKey: ['GET-ALL-REVIEWS-FOR-PROVIDER', page, limit, id],
    queryFn: ({ queryKey }) => {
      const [, pageArg = 1, limitArg = 2, idArg = id] = queryKey as [
        string,
        number?,
        number?,
        string?,
      ];
      return getAllReviewsForProvider({
        page: pageArg,
        limit: limitArg,
        id: idArg,
      });
    },
    staleTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    enabled: !!id,
  });
};

// GET PROVIDER BY ID
export const useFetchReviewOverall = (id?: string) => {
  return useQuery({
    queryKey: ['GET-REVIEW-OVERALL-PROVIDER-BY-ID', id],
    queryFn: () =>
      id ? getReviewOverall(id) : Promise.reject('No ID provided'),
    staleTime: 30 * 60 * 1000,
    enabled: !!id,
    refetchOnWindowFocus: false,
  });
};

//GET ALL PORFOLIO FOR PROVIDER
export const useFetchProviderPortfolioDetails = (
  id: string,
  page?: number,
  limit?: number
) => {
  return useQuery({
    queryKey: ['GET-ALL-PORTFOLIO-FOR-PROVIDER', id, page, limit],
    queryFn: ({ queryKey }) => {
      const [, id, pageArg = 1, limitArg = 2] = queryKey as [
        string,
        string,
        number?,
        number?,
      ];
      return getProviderPortfolioCardDetails(id, pageArg, limitArg);
    },
    staleTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    enabled: !!id,
  });
};

//GET ALL BUSINESS
export const useFetchBusinessBesttingsQuary = ({ id, keys }: any) => {
  return useQuery({
    queryKey: ['GET_ALL_BUSINESS_SETTINGS', id, keys],
    queryFn: () => getBusinessSettings({ id, keys }),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

//GET ALL CERTIFICATE
export const useFetchAllCertificate = ({
  id,
  page,
  limit,
}: {
  id?: string;
  page?: number;
  limit?: number;
}) => {
  return useQuery({
    queryKey: ['GET_ALL_CERTIFICATE', id, page, limit],
    queryFn: ({ queryKey }) => {
      const [, id, pageArg = 1, limitArg = 2] = queryKey as [
        string,
        string,
        number?,
        number?,
      ];
      return getAllProviderCertificate(id, pageArg, limitArg);
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: !!id,
    retry: false,
  });
};

// GET PROVIDER BY SLUG
export const useFetchProviderDetailsBySlug = (id?: string) => {
  return useQuery({
    queryKey: ['GET-PROVIDER-BY-SLUG', id],
    queryFn: () =>
      id ? getProviderDetailsByProvider(id) : Promise.reject('No ID provided'),
    staleTime: 30 * 60 * 1000,
    enabled: !!id,
    refetchOnWindowFocus: false,
  });
};

// GET BUSINESS DETAILS BY SLUG (public business profile via /v1/business/by-slug/:businessSlug)
export const useFetchBusinessDetailsBySlug = (slug?: string) => {
  return useQuery({
    queryKey: ['GET-BUSINESS-DETAILS-BY-SLUG', slug],
    queryFn: () =>
      slug
        ? getBusinessDetailsBySlug(slug)
        : Promise.reject('No slug provided'),
    staleTime: 30 * 60 * 1000,
    enabled: !!slug,
    refetchOnWindowFocus: false,
  });
};

// --- Multi-business support hooks (Phase 1 — per §5.5) ---

// GET BUSINESS BY SLUG
export const useFetchBusinessBySlug = (slug?: string) => {
  return useQuery({
    queryKey: ['GET-BUSINESS-BY-SLUG', slug],
    queryFn: () =>
      slug ? getBusinessBySlug(slug) : Promise.reject('No slug provided'),
    staleTime: 30 * 60 * 1000,
    enabled: !!slug,
    refetchOnWindowFocus: false,
  });
};

// GET PUBLIC SERVICES BY BUSINESS
export const useFetchPublicServicesByBusiness = ({
  data,
}: {
  data: IServiceQuery;
}) => {
  return useQuery({
    queryKey: ['GET-PUBLIC-SERVICES-BY-BUSINESS', data],
    queryFn: () =>
      getPublicServicesByBusiness({
        businessId: data.filters?.businessId ?? '',
        page: data.page,
        limit: data.limit,
      }),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    enabled: !!data.filters?.businessId,
  });
};

// GET BUSINESS PUBLIC STATS (completed jobs, etc.)
export const useFetchBusinessPublicStats = (businessId?: string) => {
  return useQuery({
    queryKey: ['GET-BUSINESS-PUBLIC-STATS', businessId],
    queryFn: () =>
      businessId
        ? getBusinessPublicStats(businessId)
        : Promise.reject('No business ID provided'),
    staleTime: 30 * 60 * 1000,
    enabled: !!businessId,
    refetchOnWindowFocus: false,
    retry: false,
  });
};

// GET REVIEWS FOR BUSINESS (uses existing review service with businessId filter)
export const useFetchBusinessReviews = (
  page?: number,
  limit?: number,
  businessId?: string
) => {
  return useQuery({
    queryKey: ['GET-BUSINESS-REVIEWS', page, limit, businessId],
    queryFn: ({ queryKey }) => {
      const [, pageArg = 1, limitArg = 10, businessIdArg] = queryKey as [
        string,
        number?,
        number?,
        string?,
      ];
      return getFilteredReviews(
        { businessId: businessIdArg },
        { page: pageArg, limit: limitArg }
      );
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    enabled: !!businessId,
  });
};

// GET REVIEW OVERALL FOR BUSINESS
export const useFetchBusinessReviewOverall = (businessId?: string) => {
  return useQuery({
    queryKey: ['GET-BUSINESS-REVIEW-OVERALL', businessId],
    queryFn: () =>
      businessId
        ? getOverallRating({ businessId })
        : Promise.reject('No business ID provided'),
    staleTime: 30 * 60 * 1000,
    enabled: !!businessId,
    refetchOnWindowFocus: false,
    retry: false,
  });
};

// Geolocation
export const useAutocomplete = (text: string) =>
  useQuery({
    queryKey: ['autocomplete', text],
    queryFn: () => autoComplete(text),
    enabled: text?.length >= 3,
    select: (data) => {
      // Ensure `features` always exists
      return {
        ...data,
        features: data?.features ?? [],
      };
    },
  });

// Landing Page — personalized by the visitor's recent search keywords
// (localStorage, max 3, most-recent-first). Keyword changes (new search /
// cleared recents) change the query key → react-query refetches.
export const useFetchLandingPage = () => {
  const { recents } = useRecentSearches();
  const keywords = recents.map((r) => r.text).slice(0, 3);
  return useQuery({
    queryKey: ['fetchLandingPage', ...(keywords.length ? [{ keywords }] : [])],
    queryFn: () => getLandingPage(keywords),
    staleTime: 5 * 60 * 1000,
  });
};

export const useFetchAllMembershipPlans = () => {
  return useQuery({
    queryKey: ['GET_ALL_MEMBERSHIP_PLANS'],
    queryFn: () => {
      return getAllMembershipPlans();
    },
    // staleTime: 5 * 60 * 1000,
    retry: false,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });
};

export const useSubmitMembershipMutation = () => {
  return useMutation({
    mutationFn: ({
      planId,
      isAnnual,
      customerEmail,
    }: {
      planId: string;
      isAnnual: boolean;
      customerEmail: string | undefined;
    }) => createSubscription(planId, isAnnual, customerEmail),
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url; // Redirect on success
      } else {
        throw new Error('Stripe session URL missing');
      }
    },
    onError: (error: any) => {
      console.error('Failed to submit membership data:', error);

      const errorMessage =
        error?.response?.data?.errors[0]?.msg ||
        'An error occurred while submitting membership data.';

      addToast({
        title: 'Error',
        description: errorMessage,
        radius: 'md',
        color: 'danger',
      });
      console.error('useSubmitMembershipMutation :', errorMessage);
    },
  });
};

export const useNotifications = (
  options: UseNotificationsOptions = {},
  filters?: {
    type?: string;
    priority?: string;
    readStatus?: string;
    sortBy?: 'newest' | 'oldest';
    search?: string;
    limit?: number;
    page?: number;
  }
) => {
  const { enabled = true } = options;

  const { user, userId } = useAuth();

  console.log('Auth data:', { userId, user });

  return useQuery({
    queryKey: [
      'notifications',
      userId,
      NOTIFICATION_ROLE,
      user?.createdAt,
      filters,
    ],
    queryFn: async () => {
      if (!userId) {
        throw new Error('User not authenticated or no roles assigned');
      }

      return getNotifications(
        userId,
        NOTIFICATION_ROLE,
        user?.createdAt,
        filters
      );
    },
    enabled: enabled && !!userId,
    refetchOnWindowFocus: false,
    retry: 1,
  });
};

export const useUnreadNotifications = () => {
  const { userId, user } = useAuth();

  return useQuery({
    queryKey: [
      'unreadNotifications',
      userId,
      NOTIFICATION_ROLE,
      user?.createdAt,
    ],
    queryFn: () => {
      if (!userId) {
        throw new Error('User not authenticated');
      }

      return getUnreadNotifications(userId, NOTIFICATION_ROLE, user?.createdAt);
    },
    enabled: !!userId,
    retry: 1,
  });
};

export const useFetchUserBySessionId = () => {
  return useQuery({
    queryKey: ['USER_SESSION'],
    queryFn: async () => {
      const sessionInfo = await getUserBySessionId();
      // Multi-business support (Phase 1) — per approved_architecture.md §4.3.
      // Persist activeBusinessId from the session so the axios interceptor can
      // attach X-Active-Business-Id header to business-scoped requests.
      if (sessionInfo?.activeBusinessId) {
        setPersistedActiveBusinessId(sessionInfo.activeBusinessId);
      } else {
        clearPersistedActiveBusinessId();
      }
      return sessionInfo;
    },
    // /auth/me is the only endpoint that silently renews the Cognito access
    // token held in the session, so this query doubles as the session
    // heartbeat. Caching it for 60 minutes — well past the access-token
    // lifetime — meant that after the token expired nothing in the SPA ever
    // revalidated it, and requests 401'd until a full page reload dropped this
    // (in-memory) cache. Keep it under the token TTL and let a window focus
    // re-arm it.
    staleTime: 5 * 60 * 1000, //5 minutes
    gcTime: 10 * 60 * 1000, //10 minutes
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: false,
    retry: (failureCount, error: any) => {
      const errorCode = error?.response?.data?.code;
      const StatusCode = error?.status;
      if (StatusCode === 404 || errorCode === 'INVALID_SESSION') {
        return false;
      }
      return failureCount < 2;
    },
  });
};

export const useCheckSessionStatus = (isAuthenticated: boolean) => {
  return useQuery({
    queryKey: ['CHECK_SESSION_STATUS'],
    queryFn: checkSessionStatus,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: 14 * 60 * 1000,
    enabled: isAuthenticated,
    retry: (failureCount, error: any) => {
      const errorCode = error?.response?.data?.code;
      const StatusCode = error?.status;
      if (errorCode === 'INVALID_SESSION') {
        addToast({
          title: 'Session Expired',
          description: 'Your session has expired. Please log in again.',

          color: 'danger',
        });
        handleLogin();
        return false;
      }
      if (StatusCode === 404) {
        return false;
      }
      return failureCount < 2;
    },
  });
};

export const useServiceSuggestions = (
  searchTerm: string,
  categoryId?: string
) => {
  return useQuery({
    queryKey: ['serviceSuggestions', searchTerm, categoryId],
    queryFn: () => getServiceSuggestions(searchTerm, categoryId),
    enabled: searchTerm?.length >= 2,
    staleTime: 1000 * 30, // 30 seconds
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });
};

// Popular / trending search suggestions for the concierge dropdown.
// Fetched once on input focus; data updates daily on the server side.
export const usePopularSearches = (enabled: boolean) => {
  return useQuery({
    queryKey: ['popularSearches'],
    queryFn: getSearchSuggestions,
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes — data updates daily, no need to refetch per keystroke
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    select: (data) => data?.data?.popular ?? [],
  });
};

// design-spec 3b: live provider counts per category for the hero's starter
// chips ("Home cleaning · 9"). Fetched once per city, cached by react-query
// so re-mounting the hero (e.g. the resume-card path) doesn't re-fetch.
export const useProwlCategoryCounts = (city: string | undefined) => {
  return useQuery({
    queryKey: ['prowlCategoryCounts', city ?? null],
    queryFn: () => getProwlCategoryCounts(city),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    // Server fails soft to null ("say nothing") — normalise to [] here so
    // every consumer can treat "no data yet" and "server said nothing" the
    // same way, without re-deriving `?? []` at every call site.
    select: (data) => data ?? [],
  });
};

// design-spec 3a/3e: the honest count behind the "Ask Prowl" button. The
// caller is responsible for debouncing `params` at 400ms idle — react-query
// itself supplies the "cached" half of the requirement, deduping identical
// params so re-resolving the same query/location pair never re-fires.
//
// Deliberately NO `placeholderData: keepPreviousData` — the design is
// explicit that the button never shows a number it doesn't have for the
// CURRENT query, so a still-in-flight refetch must read as "no count yet",
// not the previous query's stale count.
export const useProwlFacetCount = (
  params: ProwlFacetCountParams,
  enabled: boolean
) => {
  return useQuery({
    queryKey: ['prowlFacetCount', params],
    queryFn: () => getProwlFacetCount(params),
    enabled,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

//GET ALL BOOKINGS
export const useFetchAllBookingsByQuery = ({
  data,
  retry = false,
  enable = true,
}: {
  data: IBookingQuery;
  retry?: boolean | number;
  enable?: boolean;
}) => {
  return useQuery({
    queryKey: [QueryKey.GET_ALL_BOOKINGS_BY_QUERY, data],
    queryFn: () => getAllBookingsByQuery()(data),
    staleTime: 5 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry,
    enabled: enable,
    refetchOnWindowFocus: true,
  });
};

//GET ALL SERVICE
export const useFetchAllServiceByQuery = ({
  data,
  retry = false,
  enable = true,
}: {
  data: IServiceQuery;
  retry?: boolean | number;
  enable?: boolean;
}) => {
  return useQuery({
    queryKey: [QueryKey.GET_ALL_SERVICE_BY_QUERY, data],
    queryFn: () => getAllServiceByQuery()(data),
    staleTime: 5 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry,
    enabled: enable,
    refetchOnWindowFocus: false,
  });
};

//GET SINGLE SERVICE
export const useFetchServiceByQuery = ({
  data,
  retry = false,
  enable = true,
}: {
  data: IServiceQuery;
  retry?: boolean | number;
  enable?: boolean;
}) => {
  return useQuery({
    queryKey: [QueryKey.GET_SERVICE_BY_QUERY, data],
    queryFn: () => getServiceByQuery()(data),
    staleTime: 5 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry,
    enabled: enable,
    refetchOnWindowFocus: false,
  });
};

//GET ALL PUBLIC SERVICE BY PROVIDER (scoped by businessId in filters)
export const useFetchAllPublicServiceByProvider = ({
  data,
  retry = false,
  enable = true,
}: {
  data: IServiceQuery;
  retry?: boolean | number;
  enable?: boolean;
}) => {
  return useQuery({
    queryKey: [QueryKey.GET_ALL_PUBLIC_SERVICE_BY_PROVIDER, data],
    queryFn: () => getAllPublicServiceByProvider()(data),
    staleTime: 5 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry,
    enabled: enable && !!data.filters?.businessId,
    refetchOnWindowFocus: false,
  });
};

// ── Phase 2 — Quote-based booking (Job vertical) ──────────────────────────

/**
 * useFetchMyJobs — GET /v1/job/customer (customer's Job Requests).
 */
export const useFetchMyJobs = (options?: {
  enabled?: boolean;
  page?: number;
  limit?: number;
  status?: JobStatus;
}) => {
  const page = options?.page ?? 1;
  const limit = options?.limit ?? 10;
  const status = options?.status;

  return useQuery<JobsResponse>({
    queryKey: [QueryKey.MY_JOBS, page, limit, status],
    queryFn: () => getMyJobs({ page, limit, status }),
    enabled: options?.enabled ?? true,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
    placeholderData: keepPreviousData,
  });
};

/**
 * useFetchJobById — GET /v1/job/:id (Job Request detail / quote review).
 */
export const useFetchJobById = (
  jobId?: string,
  options?: { enabled?: boolean }
) => {
  return useQuery<Job>({
    queryKey: [QueryKey.JOB_DETAIL, jobId],
    queryFn: () => getJobById(jobId as string),
    enabled: !!jobId && (options?.enabled ?? true),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  });
};

/**
 * useFetchJobThread — GET /v1/job/:id/thread (negotiation thread + lock
 * state). `thread.locked` is the composer's single source of truth — never
 * derive it from `job.status` client-side.
 */
export const useFetchJobThread = (
  jobId?: string,
  options?: { enabled?: boolean }
) => {
  return useQuery<JobThread>({
    queryKey: [QueryKey.JOB_THREAD, jobId],
    queryFn: () => getJobThread(jobId as string),
    enabled: !!jobId && (options?.enabled ?? true),
    staleTime: 15 * 1000,
    refetchOnWindowFocus: true,
  });
};

/**
 * useFetchJobRevisions — GET /v1/job/:id/revisions (quote revision history,
 * ascending by revisionNo). Read-only — never gated by `isNegotiable`.
 */
export const useFetchJobRevisions = (
  jobId?: string,
  options?: { enabled?: boolean }
) => {
  return useQuery<QuoteRevision[]>({
    queryKey: [QueryKey.JOB_REVISIONS, jobId],
    queryFn: () => getJobRevisions(jobId as string),
    enabled: !!jobId && (options?.enabled ?? true),
    staleTime: 30 * 1000,
  });
};

// ── Phase 3 — Package-based booking (CustomerPackage vertical) ─────────────

/**
 * useFetchMyPackages — GET /v1/customer-package/customer (purchased packages).
 */
export const useFetchMyPackages = (options?: { enabled?: boolean }) => {
  return useQuery<CustomerPackage[]>({
    queryKey: [QueryKey.MY_CUSTOMER_PACKAGES],
    queryFn: getMyPackages,
    enabled: options?.enabled ?? true,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
    placeholderData: keepPreviousData,
  });
};
